import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { STATIC } from '../../lib/queryPresets';
import { useAuthStore } from '../../store/useAuthStore';
import {
  type CollaboratorSummary,
  type TaskAttachment,
  type TaskCommentWithAuthor,
  assignTask,
  unassignTask,
  getTaskAssignees,
  getAssignmentsByTaskIds,
  getTaskComments,
  addTaskComment,
  deleteTaskComment,
  getCommentCountsByTaskIds,
  getTaskAttachments,
  getAttachmentCountsByTaskIds,
  uploadTaskAttachment,
  deleteTaskAttachment,
  getActiveCollaborators,
} from '../../services/tasks/taskCollaborationService';
import { tasksKeys } from './useTasks';

// ============================================================
// Query keys
// ============================================================

export const taskCollabKeys = {
  assignees: (taskId: string) => ['tasks', 'assignees', taskId] as const,
  assignments: (taskIds: string[]) => ['tasks', 'assignments', [...taskIds].sort()] as const,
  comments: (taskId: string) => ['tasks', 'comments', taskId] as const,
  commentCounts: (taskIds: string[]) =>
    ['tasks', 'commentCounts', [...taskIds].sort()] as const,
  attachments: (taskId: string) => ['tasks', 'attachments', taskId] as const,
  attachmentCounts: (taskIds: string[]) =>
    ['tasks', 'attachmentCounts', [...taskIds].sort()] as const,
  collaborators: () => ['collaborators', 'active'] as const,
};

// ============================================================
// Assignations
// ============================================================

export function useTaskAssignees(taskId: string | null) {
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const session = useAuthStore((s) => s.session);
  return useQuery({
    ...STATIC,
    queryKey: taskCollabKeys.assignees(taskId ?? ''),
    queryFn: () => getTaskAssignees(taskId!),
    enabled: isInitialized && !!session && !!taskId,
  });
}

export function useTasksAssignments(taskIds: string[]) {
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const session = useAuthStore((s) => s.session);
  return useQuery({
    ...STATIC,
    queryKey: taskCollabKeys.assignments(taskIds),
    queryFn: () => getAssignmentsByTaskIds(taskIds),
    enabled: isInitialized && !!session && taskIds.length > 0,
  });
}

export function useAssignTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, assigneeId }: { taskId: string; assigneeId: string }) =>
      assignTask(taskId, assigneeId),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: taskCollabKeys.assignees(vars.taskId) });
      qc.invalidateQueries({ queryKey: ['tasks', 'assignments'] });
    },
  });
}

export function useUnassignTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, assigneeId }: { taskId: string; assigneeId: string }) =>
      unassignTask(taskId, assigneeId),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: taskCollabKeys.assignees(vars.taskId) });
      qc.invalidateQueries({ queryKey: ['tasks', 'assignments'] });
    },
  });
}

// ============================================================
// Commentaires
// ============================================================

export function useTaskComments(taskId: string | null) {
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const session = useAuthStore((s) => s.session);
  return useQuery<TaskCommentWithAuthor[]>({
    ...STATIC,
    staleTime: 15_000, // surcharge : fil de commentaires consulté en direct pendant le travail sur la tâche
    queryKey: taskCollabKeys.comments(taskId ?? ''),
    queryFn: () => getTaskComments(taskId!),
    enabled: isInitialized && !!session && !!taskId,
  });
}

export function useCommentCounts(taskIds: string[]) {
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const session = useAuthStore((s) => s.session);
  return useQuery({
    ...STATIC,
    queryKey: taskCollabKeys.commentCounts(taskIds),
    queryFn: () => getCommentCountsByTaskIds(taskIds),
    enabled: isInitialized && !!session && taskIds.length > 0,
  });
}

export function useAddTaskComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, content }: { taskId: string; content: string }) =>
      addTaskComment(taskId, content),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: taskCollabKeys.comments(vars.taskId) });
      qc.invalidateQueries({ queryKey: ['tasks', 'commentCounts'] });
    },
  });
}

export function useDeleteTaskComment(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) => deleteTaskComment(commentId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taskCollabKeys.comments(taskId) });
      qc.invalidateQueries({ queryKey: ['tasks', 'commentCounts'] });
    },
  });
}

// ============================================================
// Pièces jointes
// ============================================================

export function useTaskAttachments(taskId: string | null) {
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const session = useAuthStore((s) => s.session);
  return useQuery<TaskAttachment[]>({
    ...STATIC,
    queryKey: taskCollabKeys.attachments(taskId ?? ''),
    queryFn: () => getTaskAttachments(taskId!),
    enabled: isInitialized && !!session && !!taskId,
  });
}

export function useAttachmentCounts(taskIds: string[]) {
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const session = useAuthStore((s) => s.session);
  return useQuery({
    ...STATIC,
    queryKey: taskCollabKeys.attachmentCounts(taskIds),
    queryFn: () => getAttachmentCountsByTaskIds(taskIds),
    enabled: isInitialized && !!session && taskIds.length > 0,
  });
}

export function useUploadTaskAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, file }: { taskId: string; file: File }) =>
      uploadTaskAttachment(taskId, file),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: taskCollabKeys.attachments(vars.taskId) });
      qc.invalidateQueries({ queryKey: ['tasks', 'attachmentCounts'] });
      // Le contenu de la tâche n'a pas changé, mais on invalide quand même les listes
      // au cas où l'UI affiche des badges.
      qc.invalidateQueries({ queryKey: tasksKeys.all });
    },
  });
}

export function useDeleteTaskAttachment(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (attachmentId: string) => deleteTaskAttachment(attachmentId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taskCollabKeys.attachments(taskId) });
      qc.invalidateQueries({ queryKey: ['tasks', 'attachmentCounts'] });
    },
  });
}

// ============================================================
// Collaborators (dropdown assign)
// ============================================================

export function useActiveCollaborators() {
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const session = useAuthStore((s) => s.session);
  return useQuery<CollaboratorSummary[]>({
    ...STATIC,
    queryKey: taskCollabKeys.collaborators(),
    queryFn: getActiveCollaborators,
    enabled: isInitialized && !!session,
  });
}
